// src/components/automation/RulesList.tsx
import React from 'react';
import { AutomationRule } from '../../types/automation.types';
import { format } from 'date-fns';
import {
  Zap,
  Clock,
  Edit2,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Calendar,
  Bell,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

interface RulesListProps {
  rules: AutomationRule[];
  onEdit: (rule: AutomationRule) => void;
  onToggle: (ruleId: string, enabled: boolean) => void;
  onDelete: (ruleId: string) => void;
}

export const RulesList: React.FC<RulesListProps> = ({
  rules,
  onEdit,
  onToggle,
  onDelete
}) => {
  if (rules.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
        <Zap className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900">No automation rules yet</h3>
        <p className="text-gray-500 mt-2">
          Create your first rule to automate tasks and notifications
        </p>
      </div>
    );
  }

  const getTriggerIcon = (rule: AutomationRule) => {
    if (rule.trigger.type === 'scheduled') {
      return <Clock className="h-5 w-5 text-secondary-600" />;
    }
    return <Zap className="h-5 w-5 text-primary-600" />;
  };

  const getTriggerLabel = (rule: AutomationRule) => {
    if (rule.trigger.type === 'scheduled') {
      const schedule = rule.trigger.schedule;
      if (schedule?.type === 'relative') {
        const days = schedule.offsetDays || 0;
        const relative = schedule.relativeTo === 'appointment_date' ? 'appointment' : 'lead creation';
        if (days === -1) return `Day before ${relative}`;
        if (days === 1) return `Day after ${relative}`;
        if (days < 0) return `${Math.abs(days)} days before ${relative}`;
        return `${days} days after ${relative}`;
      }
      return `Daily at ${schedule?.time || '09:00'}`;
    }

    const eventLabels: Record<string, string> = {
      'lead_created': 'Lead created',
      'lead_assigned': 'Lead assigned',
      'lead_status_changed': 'Lead status changed',
      'appointment_created': 'Appointment created',
      'appointment_status_changed': 'Appointment status changed'
    };
    return eventLabels[rule.trigger.event || ''] || 'Event trigger';
  };

  const getActionsSummary = (rule: AutomationRule) => {
    const taskCount = rule.actions.filter(a => a.type === 'create_task').length;
    const notifCount = rule.actions.filter(a => a.type === 'send_notification' || a.type === 'send_email').length;

    const parts = [];
    if (taskCount > 0) parts.push(`${taskCount} task${taskCount > 1 ? 's' : ''}`);
    if (notifCount > 0) parts.push(`${notifCount} notification${notifCount > 1 ? 's' : ''}`);
    return parts.join(', ') || 'No actions';
  };

  return (
    <div className="space-y-4">
      {rules.map((rule) => (
        <div
          key={rule.id}
          className={`bg-white rounded-xl border-2 p-5 transition-all ${
            rule.enabled
              ? 'border-gray-100 shadow-sm hover:shadow-md'
              : 'border-gray-100 opacity-60'
          }`}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className={`p-2 rounded-lg ${
                  rule.enabled ? 'bg-primary-50' : 'bg-gray-100'
                }`}>
                  {getTriggerIcon(rule)}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{rule.name}</h3>
                  <p className="text-sm text-gray-500">{rule.description}</p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4 mt-4 text-sm">
                <div className="flex items-center gap-1.5 text-gray-600">
                  {rule.trigger.type === 'scheduled' ? (
                    <Calendar className="h-4 w-4" />
                  ) : (
                    <Zap className="h-4 w-4" />
                  )}
                  <span>{getTriggerLabel(rule)}</span>
                </div>

                <div className="flex items-center gap-1.5 text-gray-600">
                  <Bell className="h-4 w-4" />
                  <span>{getActionsSummary(rule)}</span>
                </div>

                {rule.executionStats?.totalExecutions > 0 && (
                  <div className="flex items-center gap-1.5 text-gray-600">
                    {rule.executionStats.failedExecutions > 0 ? (
                      <AlertCircle className="h-4 w-4 text-yellow-500" />
                    ) : (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    )}
                    <span>
                      {rule.executionStats.successfulExecutions} / {rule.executionStats.totalExecutions} successful
                    </span>
                  </div>
                )}

                {rule.executionStats?.lastExecutedAt && (
                  <div className="text-gray-400 text-xs">
                    Last run: {format(rule.executionStats.lastExecutedAt.toDate(), 'MMM d, yyyy h:mm a')}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 ml-4">
              <button
                onClick={() => onToggle(rule.id, !rule.enabled)}
                className={`p-2 rounded-lg transition-colors ${
                  rule.enabled
                    ? 'text-green-600 hover:bg-green-50'
                    : 'text-gray-400 hover:bg-gray-100'
                }`}
                title={rule.enabled ? 'Disable rule' : 'Enable rule'}
              >
                {rule.enabled ? (
                  <ToggleRight className="h-6 w-6" />
                ) : (
                  <ToggleLeft className="h-6 w-6" />
                )}
              </button>

              <button
                onClick={() => onEdit(rule)}
                className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                title="Edit rule"
              >
                <Edit2 className="h-5 w-5" />
              </button>

              <button
                onClick={() => onDelete(rule.id)}
                className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Delete rule"
              >
                <Trash2 className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default RulesList;
